import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {EMPTY_FINANCIAL_PERFORMANCE_RECORD, FinancialPerformanceRecord} from "../../../../../models/asset/financial-performance-record";
import {AssetValuationHistoryBase} from "../../../../../models/asset-valuation/asset-valuation-history-base";
import {TraceableDate} from "../../../../../../shared/model/traceable";
import {CodeTableEnum} from "../../../../../../shared/model/code";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AvailablePeriodDates, Period} from "../../../../../../shared/model/period";
import {selectGeneratedAvailablePeriodDates, selectRelatedFundCurrencyIsoCode, selectSelectedAsset} from "../../../../../store/asset/asset.selectors";
import {BehaviorSubject, first, Subject, switchMap, tap} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {DecimalFormatPipe} from "../../../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {Actions, ofType} from "@ngrx/effects";
import {AssetValuationDetailActions, FinancialPerformanceRecordsActions} from "../../../../../store/asset.actions";
import {AssetValuationHistoryBaseRequest} from "../../../../../generated-sources/api";
import {DateUtil} from "../../../../../../shared/utils/date-util";
import {DateTime} from "luxon";
import {
    selectAssetValuationEarningBase,
    selectFinancialPerformanceRecordsForOperational,
    selectFinancialPerformanceRecordsStatus
} from "../../../../../store/asset-valuation/asset-valuation.selectors";
import {isOfScenarioType} from "../../../../../../shared/model/scenario-type";
import {AssetValuationScenario} from "../../../../../models/asset-valuation/asset-valuation-scenario";
import {PeriodTypeEnum} from "../../../../../models/asset/period-type.enum";
import {PeriodUtil} from "../../../../../../shared/utils/period-util";
import {numberNotBiggerThan190000m} from "../../../../../../shared/utils/form-validators";
import {ClipboardUtil} from "../../../../../../shared/utils/clipboard-util";

@Component({
    selector: "valumize-asset-financial-operational-valuation-table",
    templateUrl: "./asset-financial-operational-valuation-table.component.html",
    styleUrl: "./asset-financial-operational-valuation-table.component.scss",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetFinancialOperationalValuationTableComponent implements OnInit, OnChanges {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly elementRef = inject(ElementRef);
    private readonly actions$ = inject(Actions);
    private readonly destroyRef = inject(DestroyRef);

    @Input() assetId?: number;
    @Input() assetValuationId?: number;
    @Input() scenario?: AssetValuationScenario;
    @Input() isEditable = false;
    @Input() isEditDisabled: boolean | null = null;
    @Input() exitDateAssumption: TraceableDate | null = null;
    @Input() historyBase?: AssetValuationHistoryBase;
    @Input() quickForecastOpen = false;
    @Input() showWalkToProceeds = false;
    @Output() quickForecastToggle = new EventEmitter<void>();

    // GP proceeds waterfall: synthetic, read-only field-rows appended after Common Equity, populated
    // only in the exit-year column. Values come from scenario.walkToProceeds (backend-calculated).
    static readonly WALK_OWNERSHIP = "walkOwnership";
    static readonly WALK_FUND_EQUITY = "walkFundEquity";
    static readonly WALK_FX = "walkFx";
    static readonly WALK_FUND_PROCEEDS = "walkFundProceeds";
    private static readonly WALK_FIELDS: string[] = [
        AssetFinancialOperationalValuationTableComponent.WALK_OWNERSHIP,
        AssetFinancialOperationalValuationTableComponent.WALK_FUND_EQUITY,
        AssetFinancialOperationalValuationTableComponent.WALK_FX,
        AssetFinancialOperationalValuationTableComponent.WALK_FUND_PROCEEDS
    ];
    private static readonly WALK_FIELD_SET = new Set(AssetFinancialOperationalValuationTableComponent.WALK_FIELDS);

    companyCurrencyIso = "";
    fundCurrencyIso = "";

    // A base is "custom" only if the user created it as such. After origin deletion,
    // checkAndMarkStale orphan-clears originHistoryRecordId AND sets stale=true; without
    // the !stale check those orphan-cleared bases would be misclassified as custom and
    // render via displayName instead of the period label.
    get isCustomHistoryBase(): boolean {
        return !!this.historyBase
            && !this.historyBase.annualized
            && this.historyBase.originHistoryRecordId == null
            && !this.historyBase.stale;
    }

    get historyBaseLabel(): string {
        if (!this.historyBase) {
            return "";
        }
        if (this.isCustomHistoryBase) {
            return this.historyBase.displayName;
        }
        return PeriodUtil.formatPeriodLabel(this.historyBase.period, this.historyBase.annualized);
    }

    static readonly HISTORY_BASE_COLUMN = "historyBase";
    private static readonly HISTORY_BASE_EDITABLE_FIELDS = new Set(["revenue", "netDebt", "enterpriseValue", "ebt", "ebit", "ebita", "ebitda", "ebitdar"]);

    historyBaseForm = this.formBuilder.group({
        revenue: this.formBuilder.control<number | null>(null),
        netDebt: this.formBuilder.control<number | null>(null),
        enterpriseValue: this.formBuilder.control<number | null>(null),
        ebt: this.formBuilder.control<number | null>(null),
        ebit: this.formBuilder.control<number | null>(null),
        ebita: this.formBuilder.control<number | null>(null),
        ebitda: this.formBuilder.control<number | null>(null),
        ebitdar: this.formBuilder.control<number | null>(null),
    });

    codeTableFiscalYearEnd = CodeTableEnum.SHARED_FISCALYEAREND;

    selectedAssetFinancialPerformanceData: FinancialPerformanceRecord[] = [];
    private nonExitWarningDismissedSignature: string | null = null;
    financialDataForms?: FormGroup;
    formInitializedForSave$ = new BehaviorSubject<boolean>(false);
    fiscalYearEndMonth: string | undefined;
    historyCount: FormControl<number> = new FormControl(0, {nonNullable: true});
    maxHistoryRecords: number = 1;
    // Default the History Records count to the max available on first load (records are asset-level,
    // so the max is identical across Low/Base/High). This flag ensures we seed the default only once
    // and never clobber a count the user later changes — the records subscription re-emits on calc/save.
    private historyCountInitialized = false;
    // A user-chosen History Records count is persisted per asset in localStorage (shared across
    // Low/Base/High, mirroring the fv-sensitivity column-tokens pattern), so it survives a full refresh.
    private static readonly HISTORY_COUNT_STORAGE_PREFIX = "valumize.fov-history-records.";
    selectedMetric: string | undefined;
    displayedColumns: string[] = [];
    alwaysDisplayRows = [
        "revenue",
        "revenueGrowth",
        "debtRepaymentCapacity",
        "netDebt",
        "enterpriseValue",
        "commonEquity"
    ];
    rowOrder: string[] = [
        "revenue",
        "revenueGrowth",
        "ebitdar",
        "ebitdarMargin",
        "ebitdarGrowth",
        "enterpriseValueOverEbitdarMultiple",
        "ebitda",
        "ebitdaMargin",
        "ebitdaGrowth",
        "enterpriseValueOverEbitdaMultiple",
        "ebita",
        "ebitaMargin",
        "ebitaGrowth",
        "enterpriseValueOverEbitaMultiple",
        "ebit",
        "ebitMargin",
        "ebitGrowth",
        "enterpriseValueOverEbitMultiple",
        "ebt",
        "ebtMargin",
        "ebtGrowth",
        "priceOverEarningsMultiple",
        "debtRepaymentCapacity",
        "netDebt",
        "enterpriseValue",
        "commonEquity"
    ];
    fieldDisplayNames: { [key: string]: string } = {
        revenue: "Revenue",
        revenueGrowth: "Revenue Growth",
        debtRepaymentCapacity: "Debt Repayment Capacity",
        netDebt: "Net Debt",
        enterpriseValue: "Enterprise Value",
        commonEquity: "Common Equity",
        ebt: "EBT",
        ebtMargin: "EBT Margin",
        ebtGrowth: "EBT Growth",
        ebit: "EBIT",
        ebitMargin: "EBIT Margin",
        ebitGrowth: "EBIT Growth",
        ebita: "EBITA",
        ebitaMargin: "EBITA Margin",
        ebitaGrowth: "EBITA Growth",
        ebitda: "EBITDA",
        ebitdaMargin: "EBITDA Margin",
        ebitdaGrowth: "EBITDA Growth",
        ebitdar: "EBITDAR",
        ebitdarMargin: "EBITDAR Margin",
        ebitdarGrowth: "EBITDAR Growth",
        priceOverEarningsMultiple: "P/E",
        enterpriseValueOverEbitMultiple: "EV/EBIT",
        enterpriseValueOverEbitaMultiple: "EV/EBITA",
        enterpriseValueOverEbitdaMultiple: "EV/EBITDA",
        enterpriseValueOverEbitdarMultiple: "EV/EBITDAR",
        enterpriseValueOverRevenueMultiple: "EV/Revenue"
    };
    fieldNames = Object.keys(this.fieldDisplayNames);

    // Metrics definitions for dynamic display in the table
    metrics = [
        {
            key: "ebt",
            displayName: "EBT",
            associatedRows: ["ebt", "ebtMargin", "ebtGrowth", "priceOverEarningsMultiple"]
        },
        {
            key: "ebit",
            displayName: "EBIT",
            associatedRows: ["ebit", "ebitMargin", "ebitGrowth", "enterpriseValueOverEbitMultiple"]
        },
        {
            key: "ebita",
            displayName: "EBITA",
            associatedRows: ["ebita", "ebitaMargin", "ebitaGrowth", "enterpriseValueOverEbitaMultiple"]
        },
        {
            key: "ebitda",
            displayName: "EBITDA",
            associatedRows: ["ebitda", "ebitdaMargin", "ebitdaGrowth", "enterpriseValueOverEbitdaMultiple"]
        },
        {
            key: "ebitdar",
            displayName: "EBITDAR",
            associatedRows: ["ebitdar", "ebitdarMargin", "ebitdarGrowth", "enterpriseValueOverEbitdarMultiple"]
        }
    ];

    // Phase 1.1: Cached records for table rendering
    recordsForTable: FinancialPerformanceRecord[] = [];

    // Phase 1.2: O(1) record lookup map
    private readonly recordLookup = new Map<string, FinancialPerformanceRecord>();

    // Phase 1.3: O(1) forecast form index lookup
    private readonly forecastFormIndexMap = new Map<string, number>();

    // Phase 1.4: Pre-computed period validation map
    periodValidationMap = new Map<string, boolean>();
    private availablePeriodDates: AvailablePeriodDates = {QUARTER: [], SEMESTER: [], FULL_FISCAL_YEAR: []};

    // Phase 1.5: Set-based lookups
    private static readonly RELATIVE_EDITABLE_FIELDS = new Set([
        "revenueGrowth", "debtRepaymentCapacity", "ebtMargin", "ebitMargin", "ebitaMargin", "ebitdaMargin", "ebitdarMargin", "priceOverEarningsMultiple",
        "enterpriseValueOverEbitMultiple", "enterpriseValueOverEbitaMultiple", "enterpriseValueOverEbitdaMultiple", "enterpriseValueOverEbitdarMultiple"
    ]);
    private static readonly ABSOLUTE_EDITABLE_FIELDS = new Set<string>([
        "revenue", "netDebt", "enterpriseValue"
    ]);
    private static readonly IMPORTANT_ROWS = new Set(["revenue", "netDebt", "ebt", "ebit", "ebita", "ebitda", "ebitdar"]);

    inputMode: "RELATIVE" | "ABSOLUTE" = "RELATIVE";

    // Phase 1.6: Cached exit/deletable record key dates
    exitRecordKeyDate: string | undefined;
    deletableRecordKeyDate: string | undefined;

    // Phase 3.2: Debounced calc subject
    private readonly calcSubject = new Subject<void>();

    // Set when an applyQuickForecast action fires; the next initializeForm() run patches
    // editable form controls even in edit mode, so projected per-year values (e.g. 12 %, 22 %, 32 %)
    // appear in the FOV inputs. Kept off the regular typing path to preserve the race-fix from 1612be46.
    private quickForecastPending = false;

    constructor() {
    }

    ngOnInit() {
        this.fetchFinancialPerformance();

        this.historyCount.valueChanges.pipe(
            tap(historyCount => {
                this.displayColumnsAndRows(historyCount);
                // Persist genuine user changes only. The one-time seed in updateMaxHistoryRecords uses
                // emitEvent:false, so it does not reach here and never overwrites the stored choice.
                this.persistHistoryCount(historyCount);
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        this.store.select(selectSelectedAsset).pipe(
            tap(asset => {
                this.fiscalYearEndMonth = asset.data.fiscalYearEnd.code;
                this.companyCurrencyIso = asset.data.currencyIso.code ?? "";
                this.updateExitRecordKeyDate();
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        this.store.select(selectRelatedFundCurrencyIsoCode).pipe(
            tap(fundCurrencyIso => {
                this.fundCurrencyIso = fundCurrencyIso ?? "";
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        this.store.select(selectAssetValuationEarningBase).pipe(
            tap(earningBaseCode => {
                this.selectedMetric = earningBaseCode;
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        this.store.select(selectFinancialPerformanceRecordsForOperational(this.scenario?.scenario.code)).pipe(
            tap(financialPerformanceData => {
                this.selectedAssetFinancialPerformanceData = financialPerformanceData;
                this.buildRecordLookup();
                this.rebuildPeriodValidationMap(this.availablePeriodDates);
                this.updateMaxHistoryRecords();
                this.initializeForm();
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        // Phase 1.4: Subscribe to available period dates and pre-compute validation map
        this.store.select(selectGeneratedAvailablePeriodDates).pipe(
            tap(availablePeriodDates => {
                this.availablePeriodDates = availablePeriodDates;
                this.rebuildPeriodValidationMap(availablePeriodDates);
                this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        // Phase 3.2: Debounced calc dispatch
        this.calcSubject.pipe(
            debounceTime(300),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.dispatchCalc();
        });

        this.actions$.pipe(
            ofType(AssetValuationDetailActions.applyQuickForecast),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.quickForecastPending = true;
        });

        // Debounced history base inline edit: refresh the current scenario's in-memory
        // preview. Persistence is deferred to the save() click, which bundles the pending
        // history base change with the scenario's forecasts via saveWithHistoryBase.
        this.historyBaseForm.valueChanges.pipe(
            debounceTime(500),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            if (this.isEditable && this.isCustomHistoryBase) {
                this.calc();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["historyBase"]) {
            // Only sync the form from the input while no user edits are pending. Otherwise,
            // a store update from an in-flight calc (triggered by the user's own typing)
            // re-emits a new historyBase reference here and would overwrite the value the
            // user just entered.
            if (this.historyBase && !this.historyBaseForm.dirty) {
                this.historyBaseForm.patchValue({
                    revenue: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.revenue.amount ?? undefined),
                    netDebt: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.netDebt.amount ?? undefined),
                    enterpriseValue: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.enterpriseValue.amount ?? undefined),
                    ebt: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebt.amount ?? undefined),
                    ebit: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebit.amount ?? undefined),
                    ebita: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebita.amount ?? undefined),
                    ebitda: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebitda.amount ?? undefined),
                    ebitdar: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebitdar.amount ?? undefined),
                }, {emitEvent: false});
            }
            this.displayColumnsAndRows(this.historyCount.value);
            this.cdr.markForCheck();
        }
        if (changes["showWalkToProceeds"]) {
            this.displayColumnsAndRows(this.historyCount.value);
            this.cdr.markForCheck();
        }
    }

    // Phase 1.1: Update cached recordsForTable
    private updateRecordsForTable() {
        const originId = this.historyBase?.originHistoryRecordId;

        // Exclude the origin record from the pool so the history count only
        // applies to visible records (the origin is already shown as the history base column).
        const historyRecords = this.selectedAssetFinancialPerformanceData
            .map((record, index) => ({record, index}))
            .filter(item => !item.record.forecast && !(originId != null && item.record.id === originId));

        const count = this.historyCount.value;
        const lastHistoryRecords = new Set(count === 0 ? [] : historyRecords.slice(-count).map(item => item.index));

        this.recordsForTable = this.selectedAssetFinancialPerformanceData.filter((record, index) => {
            if (record.forecast) return true;
            if (originId != null && record.id === originId) return false;
            return lastHistoryRecords.has(index);
        });
    }

    // Phase 1.2: Build record lookup map for O(1) field value access
    private buildRecordLookup() {
        this.recordLookup.clear();
        for (const record of this.selectedAssetFinancialPerformanceData) {
            const key = `${record.period.keyDate}-${record.period.type}-${record.forecast}`;
            this.recordLookup.set(key, record);
        }
    }

    // Phase 1.3: Build forecast form index map
    private buildForecastFormIndexMap() {
        this.forecastFormIndexMap.clear();
        let index = 0;
        for (const record of this.selectedAssetFinancialPerformanceData) {
            if (record.forecast) {
                this.forecastFormIndexMap.set(`${record.period.keyDate}-${record.period.type}`, index);
                index++;
            }
        }
    }

    // Phase 1.4: Pre-compute period validation map
    private rebuildPeriodValidationMap(availablePeriodDates: AvailablePeriodDates) {
        this.periodValidationMap.clear();
        for (const record of this.selectedAssetFinancialPerformanceData) {
            const date = record.period.keyDate;
            const type = record.period.type;
            if (date && type) {
                const key = `${date}-${type}`;
                if (!this.periodValidationMap.has(key)) {
                    this.periodValidationMap.set(key, this.validatePeriodDateSync(date, type, availablePeriodDates));
                }
            }
        }
    }

    private validatePeriodDateSync(date: string, type: Period["type"], availablePeriodDates: AvailablePeriodDates): boolean {
        if (type === "FULL_FISCAL_YEAR") {
            return availablePeriodDates.FULL_FISCAL_YEAR.includes(date);
        }
        if (type === "SEMESTER") {
            return availablePeriodDates.SEMESTER.includes(date);
        }
        if (type === "QUARTER") {
            return availablePeriodDates.QUARTER.includes(date);
        }
        return false;
    }

    isPeriodValid(keyDate?: string, type?: string): boolean {
        if (!keyDate || !type) {
            return false;
        }
        return this.periodValidationMap.get(`${keyDate}-${type}`) ?? false;
    }

    // Phase 1.6: Cache exit record key date
    private updateExitRecordKeyDate() {
        this.exitRecordKeyDate = this.getExitDateRecordDate(this.exitDateAssumption?.date);
    }

    // Phase 1.6: Cache deletable record key date
    private updateDeletableRecordKeyDate() {
        if (!this.financialDataForms || !this.exitDateAssumption?.date) {
            this.deletableRecordKeyDate = undefined;
            return;
        }

        const exitRecordIso = this.getExitDateRecordDate(this.exitDateAssumption.date);
        const exitRecordDate = DateUtil.fromIsoDate(exitRecordIso);
        if (!exitRecordIso || !exitRecordDate) {
            this.deletableRecordKeyDate = undefined;
            return;
        }

        const formArray = this.financialDataForms.get("records") as FormArray;
        const deleteCandidates: string[] = formArray.controls
            .map(control => control.value.periodKeyDate)
            .filter((iso: string) => {
                const dt = DateUtil.fromIsoDate(iso);
                return dt && PeriodUtil.isSameFiscalYearEndDates(iso, exitRecordIso) && dt.toMillis() > exitRecordDate.toMillis();
            });

        if (deleteCandidates.length === 0) {
            this.deletableRecordKeyDate = undefined;
            return;
        }

        deleteCandidates.sort((a, b) => {
            const millisA = DateUtil.fromIsoDate(a)?.toMillis() ?? 0;
            const millisB = DateUtil.fromIsoDate(b)?.toMillis() ?? 0;
            return millisB - millisA;
        });
        this.deletableRecordKeyDate = deleteCandidates[0];
    }

    updateMaxHistoryRecords = () => {
        this.maxHistoryRecords = this.selectedAssetFinancialPerformanceData.filter(record => !record.forecast).length;
        // Seed once history records are present: use the user's persisted choice if any (clamped to the
        // current max, since the record set may have shrunk), otherwise default to the max. emitEvent:false
        // is sufficient because the records subscription calls initializeForm() immediately after, which
        // re-renders the columns from the current historyCount value via displayColumnsAndRows().
        if (!this.historyCountInitialized && this.maxHistoryRecords > 0) {
            const persisted = this.readPersistedHistoryCount();
            const seed = persisted !== null ? Math.min(persisted, this.maxHistoryRecords) : this.maxHistoryRecords;
            this.historyCount.setValue(seed, {emitEvent: false});
            this.historyCountInitialized = true;
        }
    };

    private historyCountStorageKey(): string | null {
        return this.assetId != null
            ? `${AssetFinancialOperationalValuationTableComponent.HISTORY_COUNT_STORAGE_PREFIX}${this.assetId}`
            : null;
    }

    private readPersistedHistoryCount(): number | null {
        const key = this.historyCountStorageKey();
        if (!key) {
            return null;
        }
        try {
            const raw = localStorage.getItem(key);
            if (raw === null) {
                return null;
            }
            const parsed = Number(raw);
            return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
        } catch {
            return null;
        }
    }

    private persistHistoryCount(value: number): void {
        const key = this.historyCountStorageKey();
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, String(value));
        } catch { /* ignore storage write failures (e.g. private browsing) */ }
    }

    private displayColumnsAndRows(historyCount: number) {
        this.historyCount.setValue(historyCount, {emitEvent: false});

        this.updateRecordsForTable();

        const recordColumns = this.recordsForTable
            .filter(record => record.period.keyDate !== undefined && record.period.type !== undefined)
            .map(record => ({
                colId: `${record.period.keyDate}-${record.period.type}-${record.forecast ? "Forecast" : "History"}`,
                keyDate: record.period.keyDate as string
            }));

        if (this.historyBase?.period?.keyDate) {
            const historyBaseDate = this.historyBase.period.keyDate;
            const insertIdx = recordColumns.findIndex(rc => rc.keyDate > historyBaseDate);
            const position = insertIdx === -1 ? recordColumns.length : insertIdx;
            this.displayedColumns = ["rowName",
                ...recordColumns.slice(0, position).map(rc => rc.colId),
                AssetFinancialOperationalValuationTableComponent.HISTORY_BASE_COLUMN,
                ...recordColumns.slice(position).map(rc => rc.colId)
            ];
        } else {
            this.displayedColumns = ["rowName", ...recordColumns.map(rc => rc.colId)];
        }

        let rowsToShow = [...this.alwaysDisplayRows];
        if (this.selectedMetric) {
            const metric = this.metrics.find(m => m.key === this.selectedMetric?.toLowerCase());
            if (metric) {
                rowsToShow = rowsToShow.concat(metric.associatedRows);
            }
        }
        rowsToShow = this.sortBy(rowsToShow);
        // Walk-to-proceeds rows are appended after the sorted rows (so they sit below Common Equity)
        // and only when the toggle is on and the backend supplied the decomposition.
        if (this.showWalkToProceeds && this.scenario?.walkToProceeds) {
            rowsToShow = [...rowsToShow, ...AssetFinancialOperationalValuationTableComponent.WALK_FIELDS];
        }
        this.fieldNames = rowsToShow;
    }

    isWalkField(fieldName: string): boolean {
        return AssetFinancialOperationalValuationTableComponent.WALK_FIELD_SET.has(fieldName);
    }

    // The Traceable value backing a walk row, for the exit-year cell. The FX row is rendered
    // separately (raw multiplier + as-of date), so it returns null here.
    getWalkFieldValue(fieldName: string): any {
        const walk = this.scenario?.walkToProceeds;
        if (!walk) {
            return null;
        }
        switch (fieldName) {
            case AssetFinancialOperationalValuationTableComponent.WALK_OWNERSHIP:
                return walk.ownershipStake;
            case AssetFinancialOperationalValuationTableComponent.WALK_FUND_EQUITY:
                return walk.fundEquityCompanyCurrency;
            case AssetFinancialOperationalValuationTableComponent.WALK_FUND_PROCEEDS:
                return walk.fundProceeds;
            default:
                return null;
        }
    }

    filterForecastRecords = () => this.selectedAssetFinancialPerformanceData.filter(r => r.forecast);

    private initializeForm() {
        const forecastRecords = this.filterForecastRecords();

        // Phase 4: Patch form values instead of full rebuild when structure hasn't changed
        if (this.financialDataForms) {
            const formArray = this.financialDataForms.get("records") as FormArray;
            const existingKeyDates = formArray.controls.map(c => `${c.get("periodKeyDate")?.value}-${c.get("periodType")?.value}`);
            const newKeyDates = forecastRecords.map(r => `${r.period.keyDate}-${r.period.type}`);

            if (existingKeyDates.length === newKeyDates.length && existingKeyDates.every((k, i) => k === newKeyDates[i])) {
                if (!this.isEditable || this.quickForecastPending) {
                    forecastRecords.forEach((record, index) => {
                        const group = formArray.at(index) as FormGroup;
                        group.patchValue({
                            revenueGrowth: DecimalFormatPipe.transformFractionToPercent(record.revenueGrowth.fraction ?? undefined),
                            debtRepaymentCapacity: DecimalFormatPipe.transformFractionToPercent(record.debtRepaymentCapacity.fraction ?? undefined),
                            ebtMargin: DecimalFormatPipe.transformFractionToPercent(record.ebtMargin.fraction ?? undefined),
                            priceOverEarningsMultiple: record.priceOverEarningsMultiple.factor,
                            ebitMargin: DecimalFormatPipe.transformFractionToPercent(record.ebitMargin.fraction ?? undefined),
                            enterpriseValueOverEbitMultiple: record.enterpriseValueOverEbitMultiple.factor,
                            ebitaMargin: DecimalFormatPipe.transformFractionToPercent(record.ebitaMargin.fraction ?? undefined),
                            enterpriseValueOverEbitaMultiple: record.enterpriseValueOverEbitaMultiple.factor,
                            ebitdaMargin: DecimalFormatPipe.transformFractionToPercent(record.ebitdaMargin.fraction ?? undefined),
                            enterpriseValueOverEbitdaMultiple: record.enterpriseValueOverEbitdaMultiple.factor,
                            ebitdarMargin: DecimalFormatPipe.transformFractionToPercent(record.ebitdarMargin.fraction ?? undefined),
                            enterpriseValueOverEbitdarMultiple: record.enterpriseValueOverEbitdarMultiple.factor,
                            revenue: DecimalFormatPipe.transformFromMillionsNum(record.revenue.amount ?? undefined),
                            ebt: DecimalFormatPipe.transformFromMillionsNum(record.ebt.amount ?? undefined),
                            ebit: DecimalFormatPipe.transformFromMillionsNum(record.ebit.amount ?? undefined),
                            ebita: DecimalFormatPipe.transformFromMillionsNum(record.ebita.amount ?? undefined),
                            ebitda: DecimalFormatPipe.transformFromMillionsNum(record.ebitda.amount ?? undefined),
                            ebitdar: DecimalFormatPipe.transformFromMillionsNum(record.ebitdar.amount ?? undefined),
                            netDebt: DecimalFormatPipe.transformFromMillionsNum(record.netDebt.amount ?? undefined),
                            enterpriseValue: DecimalFormatPipe.transformFromMillionsNum(record.enterpriseValue.amount ?? undefined),
                        }, {emitEvent: false});
                    });
                    this.quickForecastPending = false;
                }

                if (this.isEditable) {
                    this.financialDataForms.enable({emitEvent: false});
                } else {
                    this.financialDataForms.disable({emitEvent: false});
                }

                this.buildForecastFormIndexMap();
                this.displayColumnsAndRows(this.historyCount.value);
                this.updateExitRecordKeyDate();
                this.updateDeletableRecordKeyDate();
                this.formInitializedForSave$.next(true);
                return;
            }
        }

        const formGroups = this.selectedAssetFinancialPerformanceData.length !== 0
            ? forecastRecords.map(record => this.createForecastFormGroup(record))
            : [];

        // Initialize the form group for financial data forms with an array of the created form groups
        this.financialDataForms = this.formBuilder.group({records: this.formBuilder.array(formGroups)});
        if (this.isEditable) {
            this.financialDataForms?.enable();
        } else {
            this.financialDataForms?.disable();
        }

        this.buildForecastFormIndexMap();
        this.displayColumnsAndRows(this.historyCount.value);
        this.updateExitRecordKeyDate();
        this.updateDeletableRecordKeyDate();
        this.formInitializedForSave$.next(true);
    }

    private createForecastFormGroup(record: FinancialPerformanceRecord): FormGroup {
        const maxValidator = numberNotBiggerThan190000m();
        return this.formBuilder.group({
            periodType: [record.period.type ?? ""],
            periodKeyDate: [record.period.keyDate ?? ""],
            revenueGrowth: [DecimalFormatPipe.transformFractionToPercent(record.revenueGrowth.fraction ?? undefined), maxValidator],
            debtRepaymentCapacity: [DecimalFormatPipe.transformFractionToPercent(record.debtRepaymentCapacity.fraction ?? undefined), maxValidator],
            ebtMargin: [DecimalFormatPipe.transformFractionToPercent(record.ebtMargin.fraction ?? undefined), maxValidator],
            priceOverEarningsMultiple: [record.priceOverEarningsMultiple.factor, maxValidator],
            ebitMargin: [DecimalFormatPipe.transformFractionToPercent(record.ebitMargin.fraction ?? undefined), maxValidator],
            enterpriseValueOverEbitMultiple: [record.enterpriseValueOverEbitMultiple.factor, maxValidator],
            ebitaMargin: [DecimalFormatPipe.transformFractionToPercent(record.ebitaMargin.fraction ?? undefined), maxValidator],
            enterpriseValueOverEbitaMultiple: [record.enterpriseValueOverEbitaMultiple.factor, maxValidator],
            ebitdaMargin: [DecimalFormatPipe.transformFractionToPercent(record.ebitdaMargin.fraction ?? undefined), maxValidator],
            enterpriseValueOverEbitdaMultiple: [record.enterpriseValueOverEbitdaMultiple.factor, maxValidator],
            ebitdarMargin: [DecimalFormatPipe.transformFractionToPercent(record.ebitdarMargin.fraction ?? undefined), maxValidator],
            enterpriseValueOverEbitdarMultiple: [record.enterpriseValueOverEbitdarMultiple.factor, maxValidator],
            revenue: [DecimalFormatPipe.transformFromMillionsNum(record.revenue.amount ?? undefined)],
            ebt: [DecimalFormatPipe.transformFromMillionsNum(record.ebt.amount ?? undefined)],
            ebit: [DecimalFormatPipe.transformFromMillionsNum(record.ebit.amount ?? undefined)],
            ebita: [DecimalFormatPipe.transformFromMillionsNum(record.ebita.amount ?? undefined)],
            ebitda: [DecimalFormatPipe.transformFromMillionsNum(record.ebitda.amount ?? undefined)],
            ebitdar: [DecimalFormatPipe.transformFromMillionsNum(record.ebitdar.amount ?? undefined)],
            netDebt: [DecimalFormatPipe.transformFromMillionsNum(record.netDebt.amount ?? undefined)],
            enterpriseValue: [DecimalFormatPipe.transformFromMillionsNum(record.enterpriseValue.amount ?? undefined)],
        });
    }

    fetchFinancialPerformance(): void {
        if (this.assetId && this.assetValuationId && this.scenario?.id && this.scenario.scenario.code && isOfScenarioType(this.scenario.scenario.code)) {
            this.store.dispatch(FinancialPerformanceRecordsActions.load({
                assetId: this.assetId,
                assetValuationId: this.assetValuationId,
                scenarioId: this.scenario.id,
                scenarioType: this.scenario.scenario.code
            }));
        }
    }

    save() {
        if (this.exitYearEquityIsNegative) return;
        const assetId = this.assetId;
        const assetValuationId = this.assetValuationId;
        const scenario = this.scenario;

        if (!assetId || !assetValuationId || !scenario?.id || !scenario.scenario.code || !isOfScenarioType(scenario.scenario.code)) {
            return;
        }

        const scenarioId = scenario.id;
        const scenarioType = scenario.scenario.code;

        this.store.select(selectFinancialPerformanceRecordsStatus(scenarioType)).pipe(
            first(status => status === "LOADED"),
            switchMap(() => this.formInitializedForSave$.pipe(
                first(initialized => initialized))
            ),
            tap(() => {
                const financialDataForms = this.financialDataForms;
                if (!financialDataForms?.valid) {
                    return;
                }
                const formRecords = (this.financialDataForms?.get("records") as FormArray).controls;

                // Ensure there are no duplicate key dates
                const keyDates = formRecords.map(group => group.get("periodKeyDate")?.value + group.get("periodType")?.value);
                const uniqueKeyDates = new Set(keyDates);
                if (uniqueKeyDates.size !== keyDates.length) {
                    return;
                }

                const recordsToSave = this.mergeFormValuesIntoRecords();
                const pendingHistoryBase = this.buildPendingHistoryBaseRequest();

                this.store.dispatch(FinancialPerformanceRecordsActions.saveWithHistoryBase({
                    assetId,
                    assetValuationId,
                    scenarioId,
                    scenarioType,
                    financialPerformanceRecords: recordsToSave,
                    inputMode: this.inputMode,
                    historyBase: pendingHistoryBase
                }));
                this.historyBaseForm.markAsPristine();
                this.formInitializedForSave$.next(false);
            }),
            // Backstop: both `first(...)` predicates may never match (e.g. the records never reach
            // LOADED, or the form is never re-initialized), which would otherwise leave this chain
            // open for the lifetime of the store and retain the component.
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    edit() {
        if (this.assetId && this.assetValuationId && this.scenario?.id && this.scenario.scenario.code && isOfScenarioType(this.scenario.scenario.code)) {
            this.store.dispatch(FinancialPerformanceRecordsActions.edit({
                assetId: this.assetId,
                assetValuationId: this.assetValuationId,
                scenarioId: this.scenario.id,
                financialPerformanceRecords: this.filterForecastRecords(),
                scenarioType: this.scenario.scenario.code,
                inputMode: this.inputMode
            }));
        }
    }

    cancel() {
        this.inputMode = "RELATIVE";
        this.resetHistoryBaseFormFromInput();
        if (this.scenario?.scenario.code && isOfScenarioType(this.scenario.scenario.code)) {
            this.fetchFinancialPerformance();
            this.store.dispatch(FinancialPerformanceRecordsActions.cancel({scenarioType: this.scenario.scenario.code}));
        }
    };

    onInputModeToggle(isAbsolute: boolean) {
        this.inputMode = isAbsolute ? "ABSOLUTE" : "RELATIVE";
        this.financialDataForms = undefined;
        this.initializeForm();
        if (this.isEditable) {
            this.calc();
        }
    }

    copy(targetScenarioType: string, sourceScenarioType: string) {
        if (this.assetValuationId && sourceScenarioType && targetScenarioType && isOfScenarioType(sourceScenarioType) && isOfScenarioType(targetScenarioType)) {
            this.store.dispatch(AssetValuationDetailActions.copyFinancialPerformanceRecords({
                assetValuationId: this.assetValuationId,
                targetScenarioType,
                sourceScenarioType
            }));
        } else {
            console.warn("Invalid scenario type(s) or missing required fields.");
        }
    }

    // Phase 1.2: O(1) field value lookup using map
    getHistoryBaseFieldValue(fieldName: string): any {
        return this.historyBase ? (this.historyBase as any)[fieldName] : null;
    }

    isHistoryBaseEditableField(fieldName: string): boolean {
        return AssetFinancialOperationalValuationTableComponent.HISTORY_BASE_EDITABLE_FIELDS.has(fieldName);
    }

    getHistoryBaseFormControl(fieldName: string): FormControl {
        return this.historyBaseForm.get(fieldName) as FormControl;
    }

    private resetHistoryBaseFormFromInput(): void {
        if (!this.historyBase) {
            this.historyBaseForm.markAsPristine();
            return;
        }
        this.historyBaseForm.patchValue({
            revenue: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.revenue.amount ?? undefined),
            netDebt: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.netDebt.amount ?? undefined),
            enterpriseValue: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.enterpriseValue.amount ?? undefined),
            ebt: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebt.amount ?? undefined),
            ebit: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebit.amount ?? undefined),
            ebita: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebita.amount ?? undefined),
            ebitda: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebitda.amount ?? undefined),
            ebitdar: DecimalFormatPipe.transformFromMillionsNum(this.historyBase.ebitdar.amount ?? undefined),
        }, {emitEvent: false});
        this.historyBaseForm.markAsPristine();
    }

    private buildPendingHistoryBaseRequest(): AssetValuationHistoryBaseRequest | undefined {
        if (!this.historyBase || !this.isCustomHistoryBase || !this.historyBaseForm.dirty) {
            return undefined;
        }
        const v = this.historyBaseForm.value;
        const hb = this.historyBase;
        return {
            ...(hb.displayName ? {displayName: hb.displayName} : {}),
            period: hb.period,
            currencyIso: {...hb.currencyIso, code: hb.currencyIso.code || undefined},
            revenue: {...hb.revenue, amount: DecimalFormatPipe.transformToMillionsNum(v.revenue)},
            netDebt: {...hb.netDebt, amount: DecimalFormatPipe.transformToMillionsNum(v.netDebt)},
            enterpriseValue: {...hb.enterpriseValue, amount: DecimalFormatPipe.transformToMillionsNum(v.enterpriseValue)},
            ebt: {...hb.ebt, amount: DecimalFormatPipe.transformToMillionsNum(v.ebt)},
            ebit: {...hb.ebit, amount: DecimalFormatPipe.transformToMillionsNum(v.ebit)},
            ebita: {...hb.ebita, amount: DecimalFormatPipe.transformToMillionsNum(v.ebita)},
            ebitda: {...hb.ebitda, amount: DecimalFormatPipe.transformToMillionsNum(v.ebitda)},
            ebitdar: {...hb.ebitdar, amount: DecimalFormatPipe.transformToMillionsNum(v.ebitdar)},
        };
    }

    getFieldValue(fieldName: string, keyDate?: string, type?: string, isForecast?: boolean): any {
        const key = `${keyDate}-${type}-${isForecast}`;
        const record = this.recordLookup.get(key);
        if (!record) return null;
        return (record as any)[fieldName];
    }

    // Phase 3.2: Route calc dispatches through debounced subject
    calc() {
        this.formInitializedForSave$.next(false);
        this.calcSubject.next();
    }

    private dispatchCalc() {
        if (!this.assetId || !this.assetValuationId || !this.scenario?.id
            || !this.scenario.scenario.code || !isOfScenarioType(this.scenario.scenario.code) || !this.financialDataForms?.valid) {
            return;
        }
        this.store.dispatch(FinancialPerformanceRecordsActions.calc({
            assetId: this.assetId,
            assetValuationId: this.assetValuationId,
            scenarioId: this.scenario.id,
            financialPerformanceRecords: this.mergeFormValuesIntoRecords(),
            scenarioType: this.scenario.scenario.code,
            inputMode: this.inputMode,
            historyBase: this.buildPendingHistoryBaseRequest()
        }));
    }

    private mergeFormValuesIntoRecords(): FinancialPerformanceRecord[] {
        const forecastRecords = this.filterForecastRecords();
        const formArray = this.financialDataForms?.get("records") as FormArray;
        if (!formArray) {
            return forecastRecords;
        }

        return formArray.controls.map((group, index) => {
            const record = forecastRecords[index];
            const transformFraction = (field: string) => DecimalFormatPipe.transformPercentToFraction(group.get(field)?.value);
            const transformFactor = (field: string) => group.get(field)?.value;
            const transformAmount = (field: string) => DecimalFormatPipe.transformToMillionsNum(group.get(field)?.value);

            const baseMerge = {
                ...record,
                period: {
                    ...record.period,
                    type: group.get("periodType")?.value,
                    keyDate: group.get("periodKeyDate")?.value
                },
            };

            if (this.inputMode === "ABSOLUTE") {
                return {
                    ...baseMerge,
                    revenue: {...record.revenue, amount: transformAmount("revenue")},
                    ebt: {...record.ebt, amount: transformAmount("ebt")},
                    ebit: {...record.ebit, amount: transformAmount("ebit")},
                    ebita: {...record.ebita, amount: transformAmount("ebita")},
                    ebitda: {...record.ebitda, amount: transformAmount("ebitda")},
                    ebitdar: {...record.ebitdar, amount: transformAmount("ebitdar")},
                    netDebt: {...record.netDebt, amount: transformAmount("netDebt")},
                    enterpriseValue: {...record.enterpriseValue, amount: transformAmount("enterpriseValue")},
                };
            }

            return {
                ...baseMerge,
                revenueGrowth: {...record.revenueGrowth, fraction: transformFraction("revenueGrowth")},
                debtRepaymentCapacity: {...record.debtRepaymentCapacity, fraction: transformFraction("debtRepaymentCapacity")},
                ebtMargin: {...record.ebtMargin, fraction: transformFraction("ebtMargin")},
                priceOverEarningsMultiple: {...record.priceOverEarningsMultiple, factor: transformFactor("priceOverEarningsMultiple")},
                ebitMargin: {...record.ebitMargin, fraction: transformFraction("ebitMargin")},
                enterpriseValueOverEbitMultiple: {...record.enterpriseValueOverEbitMultiple, factor: transformFactor("enterpriseValueOverEbitMultiple")},
                ebitaMargin: {...record.ebitaMargin, fraction: transformFraction("ebitaMargin")},
                enterpriseValueOverEbitaMultiple: {...record.enterpriseValueOverEbitaMultiple, factor: transformFactor("enterpriseValueOverEbitaMultiple")},
                ebitdaMargin: {...record.ebitdaMargin, fraction: transformFraction("ebitdaMargin")},
                enterpriseValueOverEbitdaMultiple: {...record.enterpriseValueOverEbitdaMultiple, factor: transformFactor("enterpriseValueOverEbitdaMultiple")},
                ebitdarMargin: {...record.ebitdarMargin, fraction: transformFraction("ebitdarMargin")},
                enterpriseValueOverEbitdarMultiple: {...record.enterpriseValueOverEbitdarMultiple, factor: transformFactor("enterpriseValueOverEbitdarMultiple")}
            };
        });
    }

    // Phase 1.6: Simple string comparison using cached key date
    isExitRecord = (r: FinancialPerformanceRecord) => {
        if (!r.forecast || !this.exitRecordKeyDate) {
            return false;
        }
        return r.period.keyDate === this.exitRecordKeyDate;
    };

    addForecastRecord() {
        if (!this.financialDataForms || !this.exitDateAssumption?.date) {
            return;
        }

        const exitRecordIso = this.getExitDateRecordDate(this.exitDateAssumption.date);
        const exitRecordDate = DateUtil.fromIsoDate(exitRecordIso);
        if (!exitRecordIso || !exitRecordDate) {
            return;
        }

        const formArray = this.financialDataForms.get("records") as FormArray;

        const matchingYearsForFiscalYearEnd = formArray.controls
            .map(control => control.value.periodKeyDate)
            .filter((iso: string) => PeriodUtil.isSameFiscalYearEndDates(iso, exitRecordIso))
            .map((iso: string) => DateUtil.fromIsoDate(iso)?.year)
            .filter((year): year is number => typeof year === "number");

        const lastExistingYear = matchingYearsForFiscalYearEnd.length ? Math.max(...matchingYearsForFiscalYearEnd) : exitRecordDate.year;
        const nextYear = lastExistingYear + 1;

        const nextRecordIso = DateTime.local(nextYear, exitRecordDate.month, exitRecordDate.day).toISODate() ?? undefined;
        if (!nextRecordIso) {
            return;
        }

        const defaultPeriodType = PeriodTypeEnum.FullFiscalYear;
        const newRecord: FinancialPerformanceRecord = {
            ...EMPTY_FINANCIAL_PERFORMANCE_RECORD,
            period: {...EMPTY_FINANCIAL_PERFORMANCE_RECORD.period, type: defaultPeriodType, keyDate: nextRecordIso},
            forecast: true
        };

        this.selectedAssetFinancialPerformanceData = [...this.selectedAssetFinancialPerformanceData, newRecord];
        this.buildRecordLookup();
        this.rebuildPeriodValidationMap(this.availablePeriodDates);
        this.initializeForm();
        this.cdr.markForCheck();
    }

    // Phase 1.6: Simple string comparison using cached key date
    isDeletableForecastRecord(record: FinancialPerformanceRecord) {
        if (!this.financialDataForms || !this.isEditable || !record.forecast) {
            return false;
        }
        return record.period.keyDate === this.deletableRecordKeyDate;
    }

    deleteForecastRecord(recordToDelete: FinancialPerformanceRecord) {
        this.selectedAssetFinancialPerformanceData = this.selectedAssetFinancialPerformanceData.filter(record =>
            !(record.period.keyDate === recordToDelete.period.keyDate && record.period.type === recordToDelete.period.type && record.forecast === recordToDelete.forecast)
        );
        this.buildRecordLookup();
        this.rebuildPeriodValidationMap(this.availablePeriodDates);
        this.initializeForm();
        this.cdr.markForCheck();
    }

    private getExitDateRecordDate(exitDateAssumption?: string) {
        const exitDate = DateUtil.fromIsoDate(exitDateAssumption);
        if (!exitDate) {
            return;
        }

        const fiscalYearEndForExitYear = PeriodUtil.getFiscalYearEndIsoDate(exitDate.year, this.fiscalYearEndMonth);
        const fiscalYearEndExists = fiscalYearEndForExitYear && DateUtil.fromIsoDate(fiscalYearEndForExitYear);

        const exitFFY = fiscalYearEndExists && exitDate > fiscalYearEndExists
            ? exitDate.year + 1
            : exitDate.year;

        return PeriodUtil.getFiscalYearEndIsoDate(exitFFY, this.fiscalYearEndMonth);
    }

    // Phase 1.3: O(1) form control lookup using map
    getFormControl(keyDate: string | undefined, type: string | undefined, fieldName: string): FormControl {
        const mapKey = `${keyDate}-${type}`;
        const index = this.forecastFormIndexMap.get(mapKey) ?? -1;
        const formArray = this.financialDataForms?.get("records") as FormArray;
        const control = (formArray?.at(index) as FormGroup)?.get(fieldName);

        if (!control) {
            throw new Error(`FormControl not found for keyDate: ${keyDate} and fieldName: ${fieldName}`);
        }
        return control as FormControl;
    }

    // Phase 1.5: Set-based lookup
    isFieldEditable(fieldName: string): boolean {
        if (this.inputMode === "ABSOLUTE") {
            if (AssetFinancialOperationalValuationTableComponent.ABSOLUTE_EDITABLE_FIELDS.has(fieldName)) {
                return true;
            }
            return this.selectedMetric?.toLowerCase() === fieldName;
        }
        return AssetFinancialOperationalValuationTableComponent.RELATIVE_EDITABLE_FIELDS.has(fieldName);
    }

    // Phase 1.5: Set-based lookup
    styleRowImportant(fieldName: string): string {
        if (this.isWalkField(fieldName)) {
            return fieldName === AssetFinancialOperationalValuationTableComponent.WALK_FUND_PROCEEDS ? "walk-row walk-proceeds" : "walk-row";
        }
        return AssetFinancialOperationalValuationTableComponent.IMPORTANT_ROWS.has(fieldName) ? "row-important" : "";
    }

    isZeroEquityYear(record: FinancialPerformanceRecord): boolean {
        if (!record.forecast || record.commonEquity?.amount == null || record.commonEquity?.amount > 0) {
            return false;
        }
        const forecastRecords = this.recordsForTable.filter(r => r.forecast);
        const index = forecastRecords.indexOf(record);
        const prev = forecastRecords[index - 1];
        return prev?.commonEquity?.amount == null || prev.commonEquity?.amount > 0;
    }

    get zeroEquityNonExitRecords(): FinancialPerformanceRecord[] {
        return this.recordsForTable.filter(r =>
            r.forecast &&
            !this.isExitRecord(r) &&
            r.commonEquity?.amount != null &&
            r.commonEquity.amount <= 0
        );
    }

    get showNonExitEquityWarning(): boolean {
        const records = this.zeroEquityNonExitRecords;
        return records.length > 0 && this.signatureFor(records) !== this.nonExitWarningDismissedSignature;
    }

    dismissNonExitWarning(): void {
        this.nonExitWarningDismissedSignature = this.signatureFor(this.zeroEquityNonExitRecords);
    }

    private signatureFor(records: FinancialPerformanceRecord[]): string {
        return records.map(r => r.period.keyDate ?? "").join(",");
    }

    get nonExitEquityWarningMessage(): string {
        const labels = this.zeroEquityNonExitRecords.map(r => `FY ${r.period.keyDate?.substring(0, 4)}`);
        const years = labels.length > 1
            ? labels.slice(0, -1).join(", ") + " and " + labels[labels.length - 1]
            : labels[0];

        return `Net Debt exceeds Enterprise Value in ${years}. Common Equity has been floored at 0 for these years; NAV multiple and TVPI are capped at 0× accordingly.
        Review your assumptions if this isn't intended.`;
    }

    get exitYearEquityIsNegative(): boolean {
        const exitRecord = this.recordsForTable.find(r => r.forecast && this.isExitRecord(r));
        return !!exitRecord && exitRecord.commonEquity?.amount != null && exitRecord.commonEquity.amount <= 0;
    }

    private sortBy(data: string[]): any {
        const order = this.rowOrder;
        const ordering: any = {};
        for (let i = 0; i < order.length; i++) {
            ordering[order[i]] = i;
        }
        return data.sort((a: any, b: any) => (ordering[a] - ordering[b]) || a.localeCompare(b));
    }

    // ── Excel Paste & Arrow Key Navigation ─────────────────────

    private getEditableGrid(): { rows: string[]; cols: string[] } {
        const rows = this.fieldNames.filter(f => this.isFieldEditable(f));
        const cols = this.recordsForTable
            .filter(r => r.forecast && this.isPeriodValid(r.period.keyDate, r.period.type))
            .map(r => `${r.period.keyDate}-${r.period.type}`);
        return {rows, cols};
    }

    parseClipboardNumber(raw: string): number | null {
        return ClipboardUtil.parseNumber(raw);
    }

    private splitColKey(colKey: string): [string, string] {
        return [colKey.substring(0, 10), colKey.substring(11)];
    }

    private findInputElement(fieldName: string, colKey: string): HTMLInputElement | null {
        return this.elementRef.nativeElement.querySelector(
            `input[data-field="${fieldName}"][data-col="${colKey}"]`
        );
    }

    onPaste(event: ClipboardEvent): void {
        if (!this.isEditable) {
            return;
        }

        const target = event.target as HTMLElement;
        const fieldAttr = target.getAttribute("data-field");
        const colAttr = target.getAttribute("data-col");
        if (!fieldAttr || !colAttr) {
            return;
        }

        const text = event.clipboardData?.getData("text/plain");
        if (!text) {
            return;
        }

        event.preventDefault();

        const grid = this.getEditableGrid();
        const startRow = grid.rows.indexOf(fieldAttr);
        const startCol = grid.cols.indexOf(colAttr);
        if (startRow < 0 || startCol < 0) {
            return;
        }

        const lines = text.split(/\r?\n/).filter(line => line.length > 0);
        const parsed: (number | null)[][] = lines.map(line => line.split("\t").map(cell => this.parseClipboardNumber(cell)));

        let anySet = false;
        for (let r = 0; r < parsed.length; r++) {
            const rowIdx = startRow + r;
            if (rowIdx >= grid.rows.length) {
                break;
            }
            for (let c = 0; c < parsed[r].length; c++) {
                const colIdx = startCol + c;
                if (colIdx >= grid.cols.length) {
                    break;
                }
                const value = parsed[r][c];
                if (value === null) {
                    continue;
                }
                const [keyDate, periodType] = this.splitColKey(grid.cols[colIdx]);
                const control = this.getFormControl(keyDate, periodType, grid.rows[rowIdx]);
                control.setValue(value);
                anySet = true;
            }
        }

        if (anySet) {
            this.cdr.markForCheck();
            this.calc();
        }
    }

    onGridKeydown(event: KeyboardEvent): void {
        if (!this.isEditable) {
            return;
        }

        const key = event.key;
        const isArrow = key === "ArrowUp" || key === "ArrowDown" || key === "ArrowLeft" || key === "ArrowRight";
        const isTab = key === "Tab";
        if (!isArrow && !isTab) {
            return;
        }

        const target = event.target as HTMLElement;
        const fieldAttr = target.getAttribute("data-field");
        const colAttr = target.getAttribute("data-col");
        if (!fieldAttr || !colAttr) {
            return;
        }

        const grid = this.getEditableGrid();
        let rowIdx = grid.rows.indexOf(fieldAttr);
        let colIdx = grid.cols.indexOf(colAttr);
        if (rowIdx < 0 || colIdx < 0) {
            return;
        }

        if (key === "ArrowUp") {
            rowIdx--;
        } else if (key === "ArrowDown") {
            rowIdx++;
        } else if (key === "ArrowLeft") {
            const input = target as HTMLInputElement;
            if (input.selectionStart !== 0 || input.selectionStart !== input.selectionEnd) {
                return;
            }
            colIdx--;
        } else if (key === "ArrowRight") {
            const input = target as HTMLInputElement;
            const len = input.value?.length ?? 0;
            if (input.selectionEnd !== len || input.selectionStart !== input.selectionEnd) {
                return;
            }
            colIdx++;
        } else if (isTab && event.shiftKey) {
            colIdx--;
            if (colIdx < 0) {
                rowIdx--;
                colIdx = grid.cols.length - 1;
            }
        } else if (isTab) {
            colIdx++;
            if (colIdx >= grid.cols.length) {
                rowIdx++;
                colIdx = 0;
            }
        }

        if (rowIdx < 0 || rowIdx >= grid.rows.length || colIdx < 0 || colIdx >= grid.cols.length) {
            return;
        }

        event.preventDefault();
        const targetInput = this.findInputElement(grid.rows[rowIdx], grid.cols[colIdx]);
        if (targetInput) {
            targetInput.focus();
            targetInput.select();
        }
    }

    formatDate(keyDate: string | undefined): string {
        if (!keyDate) {
            return "";
        }
        return DateTime.fromISO(keyDate).toFormat("dd/MM/yyyy");
    }
}
